import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Plus, ArrowRight, Package, ArrowsClockwise, FileText } from "@phosphor-icons/react";
import { formatCurrency } from "@/lib/currency";

const PAGE_SIZE = 50;

const StatusPill = ({ status, round }) => {
  const color = status === "closed"
    ? "border-black/20 bg-[#0B1929] text-white"
    : round > 1
      ? "border-klein-blue text-[#22D3EE]"
      : "border-black/30 text-black";
  return (
    <span className={`inline-flex items-center px-2.5 py-1 text-[10px] tracking-[0.25em] uppercase font-semibold border ${color}`}>
      {status === "closed" ? "Closed" : `Open · R${round}`}
    </span>
  );
};

export default function BopList() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/auctions", { params: { page: 1, page_size: PAGE_SIZE } });
        if (cancelled) return;
        const bop = (data.items || []).filter((a) => a.type === "bop");
        setItems(bop);
        if (isAdmin) {
          const s = await api.get("/portal/sync-status");
          if (!cancelled) setSyncStatus(s.data);
        }
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [isAdmin]);

  const triggerSync = async () => {
    setSyncing(true);
    try {
      await api.post("/portal/sync");
      const s = await api.get("/portal/sync-status");
      setSyncStatus(s.data);
    } catch {
      // silent — backend logs already
    } finally { setSyncing(false); }
  };

  const lastSync = syncStatus?.last_sync;
  const lastSyncTs = lastSync?.finished_at;

  return (
    <div className="min-h-screen bg-white" data-testid="bop-list-page">
      <TopBar />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 lg:py-16">
        <div className="grid lg:grid-cols-12 gap-8 mb-12">
          <div className="lg:col-span-8">
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3 flex items-center gap-2">
              <Package size={12} weight="bold" /> Bought-Out Parts
            </div>
            <h1 className="font-display font-extrabold text-5xl lg:text-6xl tracking-tightest leading-none">
              BOP <span className="klein-blue">Procurement</span> RFQs
            </h1>
            <p className="text-base text-neutral-600 mt-6 max-w-2xl leading-relaxed">
              Reverse auctions where bidders quote per line item. Pricing is private — only the total bid value
              is compared. Items are sourced from the upstream portal master.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3 justify-end">
            {isAdmin && (
              <Link to="/bop-auctions/new">
                <Button className="w-full rounded-none bg-[#22D3EE] hover:bg-[#0891B2] text-[#0B1929] font-semibold h-12 btn-sharp" data-testid="create-bop-cta">
                  <Plus size={16} weight="bold" className="mr-2" />
                  New BOP RFQ
                </Button>
              </Link>
            )}
            {isAdmin && (
              <div className="border border-black/10 p-3 text-xs">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-semibold">Item Cache</span>
                  <button
                    onClick={triggerSync}
                    disabled={syncing}
                    className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase font-semibold underline underline-offset-4 disabled:opacity-50"
                    data-testid="trigger-sync-btn"
                  >
                    <ArrowsClockwise size={11} weight="bold" className={syncing ? "animate-spin" : ""} />
                    {syncing ? "Syncing..." : "Sync now"}
                  </button>
                </div>
                <div className="font-mono-plex text-base" data-testid="cached-items-count">
                  {syncStatus?.cached_items ?? "—"} items
                </div>
                <div className="text-[10px] text-neutral-500 mt-1">
                  Last sync: {lastSyncTs ? new Date(lastSyncTs).toLocaleString() : "never"}
                </div>
              </div>
            )}
          </div>
        </div>

        <section data-testid="bop-auctions-section">
          <div className="flex items-baseline justify-between border-b border-black pb-3 mb-0">
            <h2 className="font-display font-extrabold text-2xl tracking-tightest">All BOP RFQs</h2>
            <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">{items.length} total</span>
          </div>
          {loading ? (
            <div className="py-10 text-xs tracking-[0.3em] uppercase text-neutral-500">Loading...</div>
          ) : items.length === 0 ? (
            <div className="border border-dashed border-black/15 p-10 text-center mt-4">
              <div className="text-sm text-neutral-600">
                No BOP RFQs yet. {isAdmin && "Create one to start procuring bought-out parts."}
              </div>
            </div>
          ) : (
            <div className="divide-y divide-black/10 border-b border-black/10">
              {items.map((a) => (
                <Link
                  to={`/auctions/${a.id}`}
                  key={a.id}
                  className="grid grid-cols-12 gap-4 py-5 hover:bg-neutral-50 transition-colors group items-center px-2"
                  data-testid={`bop-row-${a.id}`}
                >
                  <div className="col-span-12 md:col-span-6">
                    <div className="font-display font-extrabold text-xl tracking-tightest leading-tight">{a.title}</div>
                    <div className="text-sm text-neutral-600 mt-1 line-clamp-1">{a.description}</div>
                    <div className="flex gap-2 mt-1.5 text-[10px] tracking-[0.2em] uppercase text-neutral-500">
                      <span className="inline-flex items-center gap-1"><FileText size={10} weight="bold" /> {(a.line_items || []).length} line items</span>
                    </div>
                  </div>
                  <div className="col-span-6 md:col-span-2">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-1">Round</div>
                    <div className="font-mono-plex text-lg">R{a.current_round}</div>
                  </div>
                  <div className="col-span-6 md:col-span-2">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-1">{a.is_creator ? "Lowest total" : "Bids"}</div>
                    <div className="font-mono-plex text-lg">
                      {a.is_creator
                        ? (a.lowest_current_round != null ? formatCurrency(a.lowest_current_round, a.currency) : "—")
                        : (a.total_bids ?? 0)}
                    </div>
                  </div>
                  <div className="col-span-12 md:col-span-2 flex md:justify-end items-center gap-3">
                    <StatusPill status={a.status} round={a.current_round} />
                    <ArrowRight size={16} weight="bold" className="opacity-30 group-hover:opacity-100 transition-opacity" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
